import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Button, MetaRow, useToast } from '@/ui';
import { AppealModal } from '@/components/AppealModal';
import type { CaseRecord, VoteSide } from '@/core/models/case';
import { ApiError } from '@/core/lib/apiClient';
import { useCases } from '@/hooks';
import { CategoryBadge } from './CategoryBadge';
import { StatusPill } from './StatusPill';
import { VoteProgressBar } from './VoteProgressBar';
import { useCaseTarget } from '../useCaseTarget';
import { THRESHOLD } from '@/core/services/casesService';

const Card = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.glassBg};
`;
const Head = styled.div` display: flex; gap: 8px; align-items: center; `;
const HeadLink = styled(Link)`
  color: ${({ theme }) => theme.color.textPrimary}; font-weight: 600; text-decoration: none;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.focusRing}; outline-offset: 2px; }
`;
const Sub = styled.div` font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted}; `;
const Context = styled.blockquote`
  margin: 0; padding: 8px 12px; border-left: 2px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textMuted}; font-size: ${({ theme }) => theme.fontSize.sm};
`;
const Actions = styled.div` display: flex; gap: 8px; `;
const DecisionBox = styled.div`
  padding: 10px 12px; border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.bgRoot};
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};
`;
const YouVoted = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function VoteCard({ c }: { c: CaseRecord }) {
  const { castVote } = useCases();
  const show = useToast();
  const t = useCaseTarget(c);
  const votable = c.status === 'under-review' || c.status === 'appeal-review';
  const [appealing, setAppealing] = useState(false);

  // A refused vote used to do nothing at all: `void castVote(...)` with no
  // `.catch`, so the button click just vanished. That matters most on the appeal
  // path — the status pill says "Appeal Review", the buttons render, and the
  // server answers 409 because the freshly drawn appeal team has no head yet.
  // Every other vote refusal (not_on_team, and the rest) is equally silent
  // today; that is pre-existing and out of scope here.
  const submit = (side: VoteSide) => {
    // Promise.resolve() rather than a bare `.catch` on the return value: this is
    // the sole vote surface and it must not itself throw if a caller (or a test
    // double) hands back something that is not a promise.
    void Promise.resolve(castVote(c.id, side)).catch((e: unknown) => {
      if (e instanceof ApiError && e.code === 'awaiting_head_appointment') {
        show(
          'This case is waiting for a head moderator. The Council President or Vice President has to appoint one before the team can vote.',
        );
        return;
      }
      show('Could not record your vote. Try again.');
    });
  };

  return (
    <Card>
      <Head><CategoryBadge category={c.category} /><StatusPill status={c.status} /></Head>
      {t.href ? <HeadLink to={t.href}>{t.headline}</HeadLink> : <span>{t.headline}</span>}
      <Sub>
        <MetaRow>
          {t.sub.map((part) => <span key={part}>{part}</span>)}
        </MetaRow>
      </Sub>
      {c.context ? <Context>&ldquo;{c.context}&rdquo;</Context> : null}
      <VoteProgressBar votes={c.votes} />

      {votable && (
        <>
          <Actions>
            <Button
              variant={c.myVote === 'keep' ? 'primary' : 'secondary'}
              aria-pressed={c.myVote === 'keep'}
              onClick={() => { if (c.myVote !== 'keep') submit('keep'); }}
            >
              <MetaRow>
                <span>Keep</span>
                <span>{c.votes.keep}/{THRESHOLD}</span>
              </MetaRow>
            </Button>
            <Button
              variant={c.myVote === 'remove' ? 'primary' : 'secondary'}
              aria-pressed={c.myVote === 'remove'}
              onClick={() => { if (c.myVote !== 'remove') submit('remove'); }}
            >
              <MetaRow>
                <span>Remove</span>
                <span>{c.votes.remove}/{THRESHOLD}</span>
              </MetaRow>
            </Button>
          </Actions>
          {c.myVote && (
            <YouVoted>
              You voted {c.myVote === 'keep' ? 'Keep' : 'Remove'}. Click the other option to switch.
            </YouVoted>
          )}
        </>
      )}

      {c.status === 'decided' && c.decision && (
        <>
          <DecisionBox>
            <strong>
              <MetaRow>
                <span>Decision</span>
                <span>{c.decision.outcome === 'removed' ? 'Removed' : 'Kept visible'}</span>
              </MetaRow>
            </strong>
            <div>{c.decision.explanation}</div>
          </DecisionBox>
          <Actions><Button variant="ghost" onClick={() => setAppealing(true)}>Appeal decision</Button></Actions>
          {appealing && <AppealModal caseId={c.id} onClose={() => setAppealing(false)} />}
        </>
      )}

      {c.status === 'appeal-resolved' && (
        <DecisionBox>
          <strong>
            <MetaRow>
              <span>Final</span>
              <span>{c.decision?.outcome === 'removed' ? 'Removed' : 'Kept visible'}</span>
            </MetaRow>
          </strong>
          {c.decision && <div>{c.decision.explanation}</div>}
          {c.appeal && <div>{c.appeal.explanation}</div>}
        </DecisionBox>
      )}
    </Card>
  );
}
